import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { useGetFeaturedPartnersQuery } from '@/api/sanity/generated';
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs';
import { type CmsEshopItem, cmsItemsToEshops } from '@/components/boxes/EshopBox/EshopBoxes';
import { type EshopBox } from '@/components/boxes/EshopBox/types';
import { SELECT_ALL_VALUE } from '@/hooks/searchParamParsers';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { CategoriesList } from '@/sections/discounts/CategoriesList';
import { DiscountsLayout } from '@/sections/discounts/DiscountsLayout';
import { SearchResults } from '@/sections/discounts/SearchResults';
import { type Category, getCategories } from '@/sections/discounts/functions/getCategories';
import { useDiscountsSearch } from '@/sections/discounts/hooks/useDiscountsSearch';
import { useFormSearch } from '@/sections/discounts/hooks/useFormSearch';
import { getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { sanityLanguage } from '@/constants/language';
interface Props {
  categories: Category[];
  emptyCategories: Category[];
  menuPromoBanners?: PageMenuPromoBanner[];
  eshops: EshopBox[];
}
export default function Page(props) {
  const $ = _c(18);
  usePageView("discounts");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      category: SELECT_ALL_VALUE,
      fulltext: ""
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    formValues,
    sync,
    searchForm
  } = useFormSearch(t0);
  let t1;
  if ($[1] !== formValues || $[2] !== props.categories) {
    t1 = {
      formValues,
      categories: props.categories
    };
    $[1] = formValues;
    $[2] = props.categories;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    query,
    hasFilter,
    breadcrumbs
  } = useDiscountsSearch(t1);
  let t2;
  if ($[4] !== breadcrumbs || $[5] !== hasFilter || $[6] !== props.categories || $[7] !== props.emptyCategories || $[8] !== query) {
    t2 = query.data && hasFilter ? <><Breadcrumbs items={breadcrumbs} /><SearchResults emptyCategories={props.emptyCategories} query={query} /></> : <CategoriesList categories={props.categories} />;
    $[4] = breadcrumbs;
    $[5] = hasFilter;
    $[6] = props.categories;
    $[7] = props.emptyCategories;
    $[8] = query;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== formValues || $[11] !== props.categories || $[12] !== props.eshops || $[13] !== props.menuPromoBanners || $[14] !== searchForm || $[15] !== sync || $[16] !== t2) {
    t3 = <DiscountsLayout menuPromoBanners={props.menuPromoBanners} formValues={formValues} sync={sync} categories={props.categories} eshops={props.eshops} searchForm={searchForm}>{t2}</DiscountsLayout>;
    $[10] = formValues;
    $[11] = props.categories;
    $[12] = props.eshops;
    $[13] = props.menuPromoBanners;
    $[14] = searchForm;
    $[15] = sync;
    $[16] = t2;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  return t3;
}
const emptyCategoriesIds = (lang: string) => {
  switch (lang) {
    case 'cs':
      return ['elektronika', 'zdravi-a-krasa', 'deti', 'obuv', 'nabytek'];
    default:
      throw new Error(`Language ${lang} is not supported`);
  }
};
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('slevy');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  const categories = await getCategories();
  const emptyCategories = categories.filter(category => emptyCategoriesIds(ctx.locale || ctx.defaultLocale || 'cs').includes(category.id));
  const fetchFeaturedPartnersDocument = useGetFeaturedPartnersQuery.fetcher({
    type: 'popular',
    i18nLang: sanityLanguage
  });
  const featuredPartners = await fetchFeaturedPartnersDocument();
  const firstList = featuredPartners.allFeaturedPartnersList[0];
  const popularEshops = cmsItemsToEshops({
    items: firstList?.items?.filter(item => item && item.name) as CmsEshopItem[],
    imageSize: 'cover',
    size: 'md',
    index: 0,
    section: 'recommended',
    maxWidth: 293
  }).splice(0, 4);
  return {
    props: {
      categories,
      emptyCategories,
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      eshops: popularEshops,
      ...(await getIntlProps(ctx, ['discounts']))
    }
  };
}) satisfies GetStaticProps<Props>;